import { onScopeDispose, toValue, watch, type MaybeRefOrGetter } from 'vue';

// ─────────────────────────────────────────────────────────────────────────────
// Блокировка скролла страницы под оверлеями (модалки, шторки, фуллскрин-попапы).
//
// Правила (см. memory: feedback_scroll_lock_overlays):
//   1. Пока открыт любой оверлей — фон НЕ скроллится (`overflow: hidden` на body).
//   2. Фон НЕ «прыгает»: исчезновение нативного скроллбара компенсируем
//      padding-right на ширину скроллбара (как это делает headlessui под UModal).
//   3. Несколько оверлеев одновременно — счётчик ссылок: лочим на первом,
//      разлочиваем на последнем. Сохраняем/восстанавливаем исходные inline-стили.
//
// Так ведут себя auth-модалка / DynamicModal / DefaultModal (через UModal),
// поэтому кастомные оверлеи (Teleport + fixed inset-0) обязаны лочить так же.
// ─────────────────────────────────────────────────────────────────────────────

let lockCount = 0;
let savedOverflow = '';
let savedPaddingRight = '';

/** Ширина нативного вертикального скроллбара (0, если его нет — мобайл/overlay-скроллбар). */
function getScrollbarWidth(): number {
  return window.innerWidth - document.documentElement.clientWidth;
}

/**
 * Заблокировать скролл body. Идемпотентно через счётчик ссылок —
 * парный вызов `unlockBodyScroll` обязателен.
 */
export function lockBodyScroll(): void {
  if (typeof document === 'undefined') return;

  lockCount += 1;
  // Уже заблокировано другим оверлеем — стили не трогаем.
  if (lockCount > 1) return;

  const { body } = document;
  const scrollbarWidth = getScrollbarWidth();

  savedOverflow = body.style.overflow;
  savedPaddingRight = body.style.paddingRight;

  if (scrollbarWidth > 0) {
    const currentPaddingRight = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
  }
  body.style.overflow = 'hidden';
}

/**
 * Снять блокировку скролла body. Восстанавливает исходные inline-стили
 * только когда снят последний лок.
 */
export function unlockBodyScroll(): void {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) return;

  lockCount -= 1;
  if (lockCount > 0) return;

  const { body } = document;
  body.style.overflow = savedOverflow;
  body.style.paddingRight = savedPaddingRight;
}

/**
 * Реактивно лочит/разлочивает скролл, пока `source` истинно.
 * Автоматически разлочивает при размонтировании компонента/остановке scope.
 *
 * @example useScrollLock(isOpen)            // ref
 * @example useScrollLock(() => props.modelValue)
 */
export function useScrollLock(source: MaybeRefOrGetter<boolean>): void {
  let lockedByThis = false;

  const apply = (active: boolean): void => {
    if (active && !lockedByThis) {
      lockBodyScroll();
      lockedByThis = true;
    } else if (!active && lockedByThis) {
      unlockBodyScroll();
      lockedByThis = false;
    }
  };

  watch(() => toValue(source), apply, { immediate: true });

  onScopeDispose(() => {
    if (lockedByThis) {
      unlockBodyScroll();
      lockedByThis = false;
    }
  });
}
